import { startTransition, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { AppPage } from '@lilith-arcana-os/domain';

import { AppShell } from './components/ui/AppShell';
import { useAppBootstrap } from './hooks/useAppBootstrap';
import { apiClient } from './lib/api-client';
import { HistoryPage } from './pages/HistoryPage';
import { HomePage } from './pages/HomePage';
import { LibraryPage } from './pages/LibraryPage';
import { ReadingPage } from './pages/ReadingPage';
import { SettingsPage } from './pages/SettingsPage';
import { useAppStore } from './stores/app-store';
import { useUserStore } from './stores/user-store';

const renderPage = (page: AppPage) => {
  switch (page) {
    case 'reading':
      return <ReadingPage />;
    case 'library':
      return <LibraryPage />;
    case 'history':
      return <HistoryPage />;
    case 'settings':
      return <SettingsPage />;
    case 'home':
    default:
      return null;
  }
};

export default function App() {
  useAppBootstrap();

  const currentPage = useAppStore((state) => state.currentPage);
  const llmStatus = useAppStore((state) => state.llmStatus);
  const networkStatus = useAppStore((state) => state.networkStatus);
  const pwaStatus = useAppStore((state) => state.pwaStatus);
  const setCurrentPage = useAppStore((state) => state.setCurrentPage);
  const featureFlags = useAppStore((state) => state.featureFlags);
  const session = useUserStore((state) => state.session);
  const memorySummary = useUserStore((state) => state.memorySummary);
  const setMemorySummary = useUserStore((state) => state.setMemorySummary);

  const memoryQuery = useQuery({
    queryKey: ['memory'],
    queryFn: apiClient.memory,
    enabled: true,
  });

  useEffect(() => {
    if (memoryQuery.data) {
      setMemorySummary(memoryQuery.data);
    }
  }, [memoryQuery.data, setMemorySummary]);

  const handleNavigate = (page: AppPage) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  };

  const page = currentPage === 'login' ? 'home' : currentPage;

  const homePage = (
    <HomePage
      featureFlags={featureFlags}
      llmStatus={llmStatus}
      memorySummary={memorySummary}
      onNavigate={handleNavigate}
      session={session}
    />
  );

  return (
    <AppShell
      currentPage={page}
      llmStatus={llmStatus}
      networkStatus={networkStatus}
      onNavigate={handleNavigate}
      pwaStatus={pwaStatus}
      session={session}
    >
      {page === 'home' ? homePage : renderPage(page)}
    </AppShell>
  );
}
